import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Button from "../ui/Button";
import { trackSignupClick } from "../../utils/analytics";
import QRCodeModal from "../modals/QRCodeModal";

const Hero: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langPrefix = `/${i18n.language}`;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  const { scrollY } = useScroll();
  const yBackground = useTransform(scrollY, [0, 1000], [0, 200]);

  return (
    <section className="relative w-full min-h-screen bg-transparent overflow-hidden flex items-center pt-20 lg:pt-0">
      {/* Video Background - Low opacity to blend with global gradient */}
      <motion.div
        style={{ y: yBackground }}
        className="absolute inset-0 w-full h-full overflow-hidden opacity-30"
      >
        <video
          ref={videoRef}
          src="https://firebasestorage.googleapis.com/v0/b/vettconsult.firebasestorage.app/o/hero%2Fhero-web.mp4?alt=media&token=5a96f941-6c88-4290-9411-41e987be52e1"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute top-0 left-0 w-full h-full object-cover"
          onError={(e) => {
            console.error("❌ Video error event:", e);
            console.error("Video element:", videoRef.current);
            console.error("Video error code:", videoRef.current?.error?.code);
            console.error(
              "Video error message:",
              videoRef.current?.error?.message,
            );
          }}
          onLoadedData={() => {}}
          onLoadedMetadata={() => {}}
          onCanPlay={() => {}}
        >
          Tu navegador no soporta el elemento de video.
        </video>
      </motion.div>

      {/* Background Geometric Shapes (Subtle Overlay) */}
      <motion.div
        style={{ y: yBackground }}
        className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden z-0"
      >
        {/* Top Right Teal Blob */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.4, scale: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
          className="absolute -top-[20%] -right-[10%] w-[70vw] h-[70vw] rounded-full bg-[#00e9be]/10 blur-3xl"
        />
        {/* Bottom Left Dark Green Blob */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.2, scale: 1 }}
          transition={{ duration: 2, delay: 0.5, ease: "easeOut" }}
          className="absolute -bottom-[20%] -left-[10%] w-[60vw] h-[60vw] rounded-full bg-[#00342d]/10 blur-3xl"
        />
      </motion.div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left Content - Text */}
          <div className="w-full lg:w-1/2 text-left">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#00342d] mb-6 leading-[1.1]"
                style={{ fontFamily: "'Bruno Ace', sans-serif" }}
              >
                {t("seo.home.h1")
                  .split(" ")
                  .map((word, i) => (
                    <span
                      key={i}
                      className={
                        word.toUpperCase().includes("VETT") ||
                        word.toUpperCase().includes("CONSULT")
                          ? "text-[#00e9be]"
                          : ""
                      }
                    >
                      {word}{" "}
                    </span>
                  ))}
              </h1>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="text-lg sm:text-xl text-gray-600 mb-10 max-w-lg leading-relaxed font-medium"
            >
              {t("home.hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            >
              <div className="flex flex-wrap items-center gap-4">
                <Button
                  className="text-lg px-10 py-4 font-bold tracking-wide shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 text-[#00342d]"
                  onClick={() => {
                    trackSignupClick(i18n.language, "hero");
                    navigate(`${langPrefix}/signup`);
                  }}
                >
                  {t("home.hero.primaryButton")}
                </Button>
                <button
                  onClick={() => setShowQRModal(true)}
                  className="flex items-center gap-2 px-6 py-4 rounded-xl border-2 border-[#00342d] text-[#00342d] font-semibold text-base hover:bg-[#00342d] hover:text-white transition-all duration-300"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {t("appDownload.downloadApp", "Télécharger l'App")}
                </button>
              </div>
            </motion.div>

            <QRCodeModal
              isOpen={showQRModal}
              onClose={() => setShowQRModal(false)}
            />
          </div>

          {/* Right Content - Geometric Visual */}
          <div className="w-full lg:w-1/2 relative flex justify-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-3xl aspect-square lg:aspect-auto lg:h-[600px]"
            >
              {/* Abstract Dashboard Composition */}
              <div className="relative w-full h-full">
                <img
                  src="/images/home/hero/VettConsultMockup.png"
                  alt="VettConsult Dashboard"
                  className="w-full h-full object-contain"
                />

                {/* Floating Elements for Depth */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                  className="absolute -right-6 top-24 w-auto px-5 py-3 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/40 flex items-center gap-3 z-20"
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-[#00e9be] shadow-[0_0_8px_rgba(0,233,190,0.6)]"></div>
                  <span className="text-sm font-medium text-gray-800 tracking-wide">
                    {t("dashboard:soapNoteSaved")}
                  </span>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 15, 0] }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -left-6 bottom-28 w-auto px-5 py-3 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/40 flex items-center gap-3 z-20"
                >
                  <div className="w-6 h-6 rounded-full border-2 border-[#00e9be] flex items-center justify-center bg-[#00342d]">
                    <div className="w-2.5 h-2.5 bg-[#00e9be] rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-800 tracking-wide">
                    {t("dashboard:analysisReady")}
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
